'use client'

import { PageMode, Text } from '@moment/auth-ui'
import { useSearchParams } from 'next/navigation'
import { Suspense, useEffect } from 'react'

import { cn } from '@/utils/shadcn/utils'

interface LoginPageHeaderProps {
  currentMode: PageMode
  onModeChange: (mode: PageMode) => void
  showResetSuccess?: boolean
}

// Fallback component that shows immediately
const LoginPageHeaderFallback = () => (
  <Text
    as="h1"
    variant="3xl"
    className={cn(
      'text-(--color-text-primary) dark:text-(--color-text-primary-contrast)',
      'mb-9',
      'w-full',
    )}
  >
    Welcome back!
  </Text>
)

// Component that reads search params
const LoginPageHeaderContent: React.FC<LoginPageHeaderProps> = ({
  currentMode,
  onModeChange,
  showResetSuccess,
}) => {
  const searchParams = useSearchParams()
  const token = searchParams.get('token')

  // Update mode based on URL params
  useEffect(() => {
    // Don't change mode if we're showing reset success
    if (showResetSuccess) {
      return
    }

    if (token && currentMode !== PageMode.ResetPassword) {
      onModeChange(PageMode.ResetPassword)
    }
  }, [token, currentMode, onModeChange, showResetSuccess])

  const getPageContent = () => {
    // If showing reset success, always show welcome back
    if (showResetSuccess) {
      return { title: 'Welcome back!' }
    }

    switch (currentMode) {
      case PageMode.ResetRequest:
        return { title: 'Reset your password' }
      case PageMode.ResetEmailSent:
        return { title: 'Check your email' }
      case PageMode.ResetPassword:
        return { title: 'Set new password' }
      case PageMode.MagicAuth:
        return {
          title: 'Verify your login',
        }
      default:
        return { title: 'Welcome back!' }
    }
  }

  const { title } = getPageContent()

  return (
    <Text
      as="h1"
      variant="3xl"
      className={cn(
        'text-(--color-text-primary) dark:text-(--color-text-primary-contrast)',
        'mb-9',
        'w-full',
      )}
    >
      {title}
    </Text>
  )
}

// Main header component wrapped in Suspense
const LoginPageHeader: React.FC<LoginPageHeaderProps> = (props) => (
  <Suspense fallback={<LoginPageHeaderFallback />}>
    <LoginPageHeaderContent {...props} />
  </Suspense>
)

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default LoginPageHeader
